import React, { useEffect, useMemo, useRef, useState, useId, useLayoutEffect } from 'react';
import {
  Box,
  Button,
  HStack,
  IconButton,
  Portal,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { LuX } from 'react-icons/lu';
import { useSchedules } from '../query/useSchedules';

const STORAGE_KEY = 'ekkos.onboardingDone';
const SUGGESTED_PHRASE = 'remind me to complete the task in 45 seconds';
const STEP_THREE_FAILSAFE_MS = 60 * 1000;

function useElementRect(targetId, deps = []) {
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (typeof window === 'undefined' || !targetId) return;
    const el = document.getElementById(targetId);
    if (!el) {
      setRect(null);
      return;
    }

    const computeRect = () => {
      const box = el.getBoundingClientRect();
      setRect({
        top: box.top,
        left: box.left,
        width: box.width,
        height: box.height,
        bottom: box.bottom,
        right: box.right,
      });
    };

    computeRect();
    window.addEventListener('resize', computeRect);
    window.addEventListener('scroll', computeRect, true);
    let observer = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(computeRect);
      observer.observe(el);
    }

    return () => {
      window.removeEventListener('resize', computeRect);
      window.removeEventListener('scroll', computeRect, true);
      observer?.disconnect();
    };
  }, [targetId, ...deps]);

  return rect;
}

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

export default function OnboardingCoach({
  inputValue,
  onInsertSuggestion,
  submissionSignal,
  composerInputId = 'command-input',
  addButtonId = 'command-add',
}) {
  const coachLabelId = useId();
  const { data: schedules = [], isLoading } = useSchedules();
  const [hasCompleted, setHasCompleted] = useState(() => {
    if (typeof window === 'undefined') return true;
    try {
      return window.localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return true;
    }
  });
  const [step, setStep] = useState('focusInput');
  const [visible, setVisible] = useState(false);
  const [pendingScheduleCallout, setPendingScheduleCallout] = useState(false);
  const [scheduleHighlightId, setScheduleHighlightId] = useState(null);
  const [awaitingReminder, setAwaitingReminder] = useState(false);
  const [lastScheduleRect, setLastScheduleRect] = useState(null);
  const [forceRun, setForceRun] = useState(false);
  const timeoutRef = useRef(null);
  const lastSubmission = useRef(null);
  const scheduleTriggerRef = useRef(null);
  const inputRect = useElementRect(composerInputId, [visible]);
  const addRect = useElementRect(addButtonId, [visible]);
  const scheduleRect = useElementRect(
    scheduleHighlightId ? `schedule-${scheduleHighlightId}` : null,
    [scheduleHighlightId, schedules, step]
  );
  const shadeColor = useColorModeValue('rgba(0,0,0,0.45)', 'rgba(0,0,0,0.65)');
  const calloutBg = useColorModeValue('#fdfbff', 'rgba(13,21,32,0.95)');
  const calloutTextColor = useColorModeValue('gray.800', 'white');
  const accentColor = useColorModeValue('#2563eb', '#63b3ed');
  const calloutGradient = useColorModeValue(
    'linear-gradient(135deg, #fdfbff, #f4f8ff)',
    'linear-gradient(135deg, rgba(13,21,32,0.97), rgba(19,34,52,0.98))'
  );
  const arrowFill = useColorModeValue('#fdfbff', 'rgba(19,34,52,0.98)');
  const calloutRef = useRef(null);
  const [calloutSize, setCalloutSize] = useState({ width: 0, height: 0 });

  const zeroTasks = (schedules?.length || 0) === 0;
  const shouldShow = (forceRun || (!hasCompleted && zeroTasks)) && !awaitingReminder;
  const shouldForceSchedule = step === 'showSchedule' && !!scheduleHighlightId;
  const dismissButtonProps = {
    size: 'sm',
    variant: 'outline',
    colorScheme: 'blue',
    _hover: { bg: 'whiteAlpha.200' },
    _focus: { boxShadow: 'outline' },
  };

  const completeOnboarding = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    scheduleTriggerRef.current = null;
    setVisible(false);
    setPendingScheduleCallout(false);
    setScheduleHighlightId(null);
    setAwaitingReminder(false);
    setStep('focusInput');
    setHasCompleted(true);
    setForceRun(false);
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(STORAGE_KEY, '1');
      } catch {
        /* no-op */
      }
    }
  };

  const dismiss = () => {
    completeOnboarding();
  };

  useEffect(() => {
    if (shouldForceSchedule) {
      setVisible(true);
      return;
    }
    if (shouldShow && !isLoading && !pendingScheduleCallout) {
      setVisible(true);
    } else {
      setVisible(false);
    }
  }, [shouldShow, isLoading, shouldForceSchedule, pendingScheduleCallout]);

  useEffect(() => {
    if (!visible) return;
    if (step === 'showSchedule') return;
    if (inputValue?.trim()) {
      setStep('pointAdd');
    } else {
      setStep('focusInput');
    }
  }, [inputValue, visible, step]);

  useEffect(() => {
    if (!submissionSignal) return;
    const submissionId =
      typeof submissionSignal === 'object' && submissionSignal !== null
        ? submissionSignal.id
        : submissionSignal;
    if (!submissionId || submissionId === lastSubmission.current) return;
    if (hasCompleted) return;
    const zeroTasksBeforeSubmit =
      typeof submissionSignal === 'object' && submissionSignal !== null && 'zeroTasksBeforeSubmit' in submissionSignal
        ? Boolean(submissionSignal.zeroTasksBeforeSubmit)
        : true;
    if (!forceRun && !zeroTasksBeforeSubmit) return;
    lastSubmission.current = submissionId;
    scheduleTriggerRef.current = forceRun ? 'force' : 'first-task';
    setVisible(false);
    setScheduleHighlightId(null);
    setPendingScheduleCallout(true);
    setAwaitingReminder(true);
    setStep('focusInput');
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => completeOnboarding(), STEP_THREE_FAILSAFE_MS);
  }, [submissionSignal, hasCompleted, forceRun]);

  useEffect(() => {
    if (!pendingScheduleCallout) return;
    if (hasCompleted) {
      setPendingScheduleCallout(false);
      return;
    }
    const trigger = scheduleTriggerRef.current;
    if (!trigger) {
      setPendingScheduleCallout(false);
      return;
    }
    if (trigger !== 'force' && !forceRun && trigger !== 'first-task') {
      setPendingScheduleCallout(false);
      return;
    }
    if (!schedules || schedules.length === 0) return;
    const first = schedules[0];
    if (!first?.originJobKey) return;
    let cancelled = false;
    let raf = null;
    const waitForElement = () => {
      if (cancelled) return;
      const el = document.getElementById(`schedule-${first.originJobKey}`);
      if (el) {
        setScheduleHighlightId(first.originJobKey);
        setStep('showSchedule');
        setPendingScheduleCallout(false);
        return;
      }
      raf = requestAnimationFrame(waitForElement);
    };
    raf = requestAnimationFrame(waitForElement);
    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
    };
  }, [pendingScheduleCallout, schedules, hasCompleted, forceRun]);

  useEffect(() => {
    if (!scheduleHighlightId) return;
    const el = document.getElementById(`schedule-${scheduleHighlightId}`);
    el?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  }, [scheduleHighlightId]);

  useEffect(() => {
    if (step !== 'showSchedule') return;
    if (scheduleHighlightId) {
      const existing = document.getElementById(`schedule-${scheduleHighlightId}`);
      if (existing) return;
    }
    const fallback = schedules?.[0];
    if (fallback?.originJobKey && fallback.originJobKey !== scheduleHighlightId) {
      setScheduleHighlightId(fallback.originJobKey);
    }
  }, [step, schedules, scheduleHighlightId]);

  useEffect(() => {
    if (scheduleRect) {
      setLastScheduleRect(scheduleRect);
    }
  }, [scheduleRect]);

  useEffect(() => {
    if (step !== 'showSchedule') {
      setLastScheduleRect(null);
    }
  }, [step]);

  useEffect(() => {
    if (!awaitingReminder) return;
    if (schedules.some((item) => item.isFiring)) {
      completeOnboarding();
    }
  }, [awaitingReminder, schedules]);

  useEffect(() => {
    if (!visible) return;
    const handler = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        dismiss();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [visible]);

  // Previously we snapped focus back to the composer during coaching steps, but
  // on mobile (especially iOS) that caused the keyboard to pop up and obscure the UI.
  // Leave focus wherever the user last placed it to avoid layout jumps.

  useEffect(() => () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleReset = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      scheduleTriggerRef.current = null;
      setForceRun(true);
      setHasCompleted(false);
      setVisible(true);
      setStep('focusInput');
      setPendingScheduleCallout(false);
      setScheduleHighlightId(null);
      setAwaitingReminder(false);
      setLastScheduleRect(null);
    };
    window.addEventListener('ekkos:onboardingReset', handleReset);
    return () => window.removeEventListener('ekkos:onboardingReset', handleReset);
  }, []);

  useEffect(() => {
    const input = document.getElementById(composerInputId);
    const button = document.getElementById(addButtonId);
    const scheduleTarget = scheduleHighlightId
      ? document.getElementById(`schedule-${scheduleHighlightId}`)
      : null;
    const applyAria = () => {
      if (!visible) {
        input?.removeAttribute('aria-describedby');
        button?.removeAttribute('aria-describedby');
        scheduleTarget?.removeAttribute('aria-describedby');
        return;
      }
      if (step === 'focusInput') {
        input?.setAttribute('aria-describedby', coachLabelId);
        button?.removeAttribute('aria-describedby');
        scheduleTarget?.removeAttribute('aria-describedby');
      } else if (step === 'pointAdd') {
        button?.setAttribute('aria-describedby', coachLabelId);
        input?.removeAttribute('aria-describedby');
        scheduleTarget?.removeAttribute('aria-describedby');
      } else if (step === 'showSchedule') {
        scheduleTarget?.setAttribute('aria-describedby', coachLabelId);
        input?.removeAttribute('aria-describedby');
        button?.removeAttribute('aria-describedby');
      } else {
        input?.removeAttribute('aria-describedby');
        button?.removeAttribute('aria-describedby');
        scheduleTarget?.removeAttribute('aria-describedby');
      }
    };
    applyAria();
    return () => {
      input?.removeAttribute('aria-describedby');
      button?.removeAttribute('aria-describedby');
      scheduleTarget?.removeAttribute('aria-describedby');
    };
  }, [visible, composerInputId, addButtonId, coachLabelId, step, scheduleHighlightId]);

  const activeScheduleRect = scheduleRect || lastScheduleRect;
  const calloutRect = step === 'focusInput'
    ? inputRect
    : step === 'pointAdd'
      ? addRect
      : activeScheduleRect;

  useLayoutEffect(() => {
    if (!visible) return;
    const node = calloutRef.current;
    if (!node) return;
    const measure = () => {
      setCalloutSize({
        width: node.offsetWidth || 0,
        height: node.offsetHeight || 0,
      });
    };
    measure();
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(measure);
      observer.observe(node);
      return () => observer.disconnect();
    }
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [visible, step]);

  const calloutPosition = useMemo(() => {
    if (!calloutRect || typeof window === 'undefined') return null;
    const width = calloutSize.width || 280;
    const height = calloutSize.height || 160;
    const hasRoomAbove = calloutRect.top >= height + 32;
    const desiredTop = hasRoomAbove ? calloutRect.top - height - 16 : calloutRect.bottom + 16;
    const clampedTop = clamp(desiredTop, 16, window.innerHeight - height - 16);
    const center = calloutRect.left + calloutRect.width / 2;
    const desiredLeft = center - width / 2;
    const clampedLeft = clamp(desiredLeft, 16, window.innerWidth - width - 16);
    return { top: clampedTop, left: clampedLeft, placement: hasRoomAbove ? 'top' : 'bottom' };
  }, [calloutRect, calloutSize]);

  if (!visible || !calloutRect) return null;

  const highlightRect = calloutRect;

  const insertSuggestion = () => {
    onInsertSuggestion?.(SUGGESTED_PHRASE);
    setStep('pointAdd');
    requestAnimationFrame(() => {
      // document.getElementById(composerInputId)?.focus?.();
    });
  };

  const acknowledgeScheduleStep = () => {
    completeOnboarding();
  };

  const stepLabel = step === 'showSchedule' ? 'Step 3 of 3' : (step === 'pointAdd' ? 'Step 2 of 3' : 'Step 1 of 3');

  return (
    <Portal>
      <Box position="fixed" inset={0} zIndex={2000} pointerEvents="none">
        <Box position="absolute" inset={0} bg={shadeColor} pointerEvents="none" />
        {highlightRect && (
          <Box
            position="fixed"
            top={`${highlightRect.top - 8}px`}
            left={`${highlightRect.left - 8}px`}
            width={`${highlightRect.width + 16}px`}
            height={`${highlightRect.height + 16}px`}
            borderRadius="16px"
            border="2px solid #63b3ed"
            boxShadow="0 0 20px rgba(99, 179, 237, 0.6)"
            pointerEvents="none"
            transition="all 0.2s ease"
          />
        )}
        {calloutPosition && (
          <Box
            ref={calloutRef}
            pointerEvents="auto"
            position="fixed"
            top={`${calloutPosition.top}px`}
            left={`${calloutPosition.left}px`}
            maxW="320px"
            w="min(320px, calc(100vw - 32px))"
            bg={calloutBg}
            color={calloutTextColor}
            borderRadius="xl"
            boxShadow="0 25px 55px rgba(0,0,0,0.5)"
            p={4}
            border="1px solid rgba(99,179,237,0.35)"
            role="dialog"
            aria-modal="false"
            aria-live="polite"
            id={coachLabelId}
            data-coach-focus="true"
            zIndex={2100}
            sx={{
              backgroundImage: calloutGradient,
              position: 'fixed',
              animation: 'coach-pop 220ms cubic-bezier(0.2,0.8,0.4,1)',
              '&::after': {
                content: '""',
                position: 'absolute',
                width: 0,
                height: 0,
                left: '40%',
                transform: 'translateX(-50%)',
                borderLeft: '10px solid transparent',
                borderRight: '10px solid transparent',
                filter: 'drop-shadow(0 6px 10px rgba(0,0,0,0.45))',
                ...(calloutPosition.placement === 'top'
                  ? {
                    top: '100%',
                    borderTop: `12px solid ${arrowFill}`,
                  }
                  : {
                    bottom: '100%',
                    borderBottom: `12px solid ${arrowFill}`,
                  }),
              },
              '@keyframes coach-pop': {
                '0%': { opacity: 0, transform: 'translateY(8px) scale(0.97)' },
                '100%': { opacity: 1, transform: 'translateY(0) scale(1)' },
              },
            }}
          >
            <HStack justify="space-between" mb={2}>
              <Text fontSize="xs" textTransform="uppercase" letterSpacing="wide" color={accentColor}>
                {stepLabel}
              </Text>
              <IconButton
                icon={<LuX />}
                aria-label="Dismiss onboarding"
                size="sm"
                variant="outline"
                colorScheme="whiteAlpha"
                onClick={dismiss}
                _hover={{ bg: 'whiteAlpha.200' }}
                _focus={{ boxShadow: 'outline' }}
              />
            </HStack>
            {step === 'focusInput' ? (
              <VStack align="flex-start" spacing={3}>
                <Text fontWeight="semibold">
                  Type:{' '}
                  <Text as="span" fontStyle="italic">
                    {SUGGESTED_PHRASE}
                  </Text>
                  <Text fontSize="sm" color="whiteAlpha.800">
                    Ekkos schedules by natural language like in one day, next Tuesday, or weekly on Fridays.
                  </Text>
                </Text>
                <Button size="sm" colorScheme="blue" onClick={insertSuggestion}>
                  Insert suggestion
                </Button>
                <Button onClick={dismiss} {...dismissButtonProps}>
                  Dismiss
                </Button>
              </VStack>
            ) : step === 'pointAdd' ? (
              <VStack align="flex-start" spacing={3}>
                <Text fontWeight="semibold">
                  Click <Text as="span" color="blue.200">Add</Text> to schedule it.
                </Text>
                {/* <Text fontSize="sm" color="whiteAlpha.800">
                  After it saves, we’ll point to the task so you can see it in the list.
                </Text> */}
                <Button onClick={dismiss} {...dismissButtonProps}>
                  Dismiss
                </Button>
              </VStack>
            ) : (
              <VStack align="flex-start" spacing={3}>
                <Text fontWeight="semibold">
                  Here’s your task! It will alert at the scheduled time.
                </Text>
                <Text fontSize="sm" color="whiteAlpha.800">
                  Tap the card to open details or mark it done once the notification arrives.
                  Remember to:
                  <ul style={{ marginTop: '4px', paddingLeft: '20px' }}>
                    <li>Unblock notifications</li>
                    <li>Head to the settings page to manage device notifications, follow ups, and personality</li>
                    <li>Add your own tasks!</li>
                  </ul>
                </Text>
                <HStack spacing={3}>
                  <Button size="sm" colorScheme="blue" onClick={acknowledgeScheduleStep}>
                    Got it
                  </Button>
                  <Button onClick={dismiss} {...dismissButtonProps}>
                    Dismiss
                  </Button>
                </HStack>
              </VStack>
            )}
          </Box>
        )}
      </Box>
    </Portal>
  );
}
